import { Injectable, inject, signal } from '@angular/core';
import {
    ECustomPageEmbeddedMessageType,
    ICustomPageEmbeddedSendDataPostMessage,
    Json,
    isCustomPageEmbeddedSendDataPostMessage,
    isJsonObject,
    isStringArray,
} from '@se/cp.common';
import { Logger } from '@se/se.platform.base';
import { isString, uniq } from 'lodash-es';
import { Observable, catchError, filter, finalize, forkJoin, fromEvent, map, of, switchMap } from 'rxjs';
import { EstensaWaitingService } from '../../services/waiting.service';
import { CpHttpService } from './cp-http.service';
import { ICustomPageLayoutData, ICustomPageMasterData, ICustomPageMasterDataResolver } from './master-data';
import { filterConstraintsByDisabler, mergeDataForResearchArchive } from './utils';

@Injectable({
    providedIn: 'root',
})
export class EmbeddedMasterDataResolver implements ICustomPageMasterDataResolver {
    private readonly cpHttpService = inject(CpHttpService);
    private readonly waitingService = inject(EstensaWaitingService);
    private readonly logger = inject(Logger);
    readonly isReady = signal<boolean>(false);
    private idCustomPageRoot = '';
    private idExaminations: string[] = [];
    private idModelTypes: string[] = [];

    getCustomPageRoot() {
        return this.idCustomPageRoot;
    }

    getModelTypes() {
        return this.idModelTypes;
    }

    getData() {
        return this.cpHttpService.getData(this.idExaminations, this.idCustomPageRoot) as any as Observable<Json>;
    }

    getLayout(idCustomPage: string): Observable<ICustomPageLayoutData> {
        return forkJoin([
            this.cpHttpService.getDataset(idCustomPage),
            this.cpHttpService.getLayout(idCustomPage),
            this.cpHttpService.getItemSourcesWithItemsByIdCustomPage(idCustomPage),
            this.cpHttpService.getPlaceholdersByIdCustomPage(idCustomPage),
        ]).pipe(
            map(([dataset, layout, dataSources, placeholders]) => ({
                constraints: filterConstraintsByDisabler(layout?.jsonConstraint?.array ?? []),
                dataset,
                dataSources: dataSources?.jsonItemsSource ?? {},
                idModelTypes: this.idModelTypes,
                layout: layout?.jsonPanel,
                placeholders: placeholders?.jsonDefaultValue,
            })),
        );
    }

    getMasterData(): Observable<ICustomPageMasterData> {
        this.waitingService.isWaiting = true;
        return fromEvent<MessageEvent<ICustomPageEmbeddedSendDataPostMessage>>(window, 'message').pipe(
            filter((e) => {
                const embeddedData = e.data;
                return (
                    isCustomPageEmbeddedSendDataPostMessage(embeddedData) &&
                    e.data.messageType === ECustomPageEmbeddedMessageType.sendData
                );
            }),
            map((e) => ({
                idExaminations: e.data.data.idExaminations ?? [],
                idCustomPageRoot: e.data.data.idCustomPageRoot ?? '',
                researchArchive: !!e.data.data.researchArchive,
                exportRequests: e.data.data.exportRequests ?? [],
            })),
            switchMap(({ idExaminations, idCustomPageRoot, researchArchive, exportRequests }) => {
                this.idExaminations = idExaminations;
                this.idCustomPageRoot = idCustomPageRoot;
                return this.cpHttpService.getData(idExaminations, idCustomPageRoot, false, researchArchive).pipe(
                    switchMap((model) => {
                        const idModelTypeList: string[] =
                            isJsonObject(model) && isStringArray(model.IdModelTypeList) ? model.IdModelTypeList : [];
                        const idModelTypes = uniq(idModelTypeList);
                        this.idModelTypes = idModelTypes;
                        const idCustomPage =
                            isJsonObject(model) && isString(model.IdCustomPage) ? model.IdCustomPage : '';
                        return this.getLayout(idCustomPage).pipe(
                            map((layoutData) => {
                                const dataset = layoutData.dataset;
                                if (researchArchive && exportRequests.length && isJsonObject(model)) {
                                    model = mergeDataForResearchArchive(model, dataset, exportRequests);
                                }
                                return {
                                    ...layoutData,
                                    idCustomPage: idCustomPage,
                                    idModelTypes: this.idModelTypes,
                                    model: { Report: model },
                                };
                            }),
                        );
                    }),
                    catchError((error) => {
                        this.logger.error(error);
                        return of(error);
                    }),
                );
            }),
            finalize(() => {
                this.waitingService.isWaiting = false;
                this.isReady.set(true);
            }),
        );
    }
}
